/**
 * Typed reader for the client's VITE_ env vars. Fails loudly in dev if a
 * required value is missing so we don't paper over misconfiguration by
 * sending undefined to Auth0 or the API.
 */
function required(name: string, value: string | undefined): string {
  if (!value || value.length === 0) {
    // Keep this as a warning rather than throw during module init; the Login
    // page surfaces a user-visible banner instead so the dev can see it in
    // the UI rather than a white screen.
    if (import.meta.env.DEV) {
      console.warn(`[env] ${name} is not set`);
    }
    return '';
  }
  return value;
}

export const env = {
  apiBaseUrl: required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
  auth0Domain: required('VITE_AUTH0_DOMAIN', import.meta.env.VITE_AUTH0_DOMAIN),
  auth0ClientId: required('VITE_AUTH0_CLIENT_ID', import.meta.env.VITE_AUTH0_CLIENT_ID),
  auth0Audience: required('VITE_AUTH0_AUDIENCE', import.meta.env.VITE_AUTH0_AUDIENCE),
  auth0GoogleConnection: import.meta.env.VITE_AUTH0_GOOGLE_CONNECTION ?? 'google-oauth2',
  // Auth0 database connection name used for the password-change flow.
  // Defaults to the out-of-the-box connection Auth0 ships with — set
  // `VITE_AUTH0_DB_CONNECTION` if your tenant uses a custom name.
  auth0DbConnection: import.meta.env.VITE_AUTH0_DB_CONNECTION ?? 'Username-Password-Authentication',
  isAuth0Configured:
    !!import.meta.env.VITE_AUTH0_DOMAIN &&
    !!import.meta.env.VITE_AUTH0_CLIENT_ID &&
    !!import.meta.env.VITE_AUTH0_AUDIENCE,
  // GA4 — optional. Empty string disables analytics; analytics.ts no-ops.
  gaMeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID ?? '',
  /**
   * Base URL used to build the shareable affiliate link (`<base>/invite?ref=<code>`).
   * Falls back to `window.location.origin` at use-site when unset, which is
   * correct for local dev but not for sharing — production should set
   * `VITE_AFFILIATE_BASE_URL=https://app.olliereads.org`.
   */
  affiliateBaseUrl: import.meta.env.VITE_AFFILIATE_BASE_URL ?? '',
  /**
   * Store links surfaced by the mobile gate (`MobileAppSplash`). Phone
   * and touch-iPad users are steered to the native app instead of the
   * desktop-tuned web SPA. Empty string hides the corresponding button.
   */
  iosAppStoreUrl: import.meta.env.VITE_IOS_APP_STORE_URL ?? '',
  androidPlayStoreUrl: import.meta.env.VITE_ANDROID_PLAY_STORE_URL ?? '',
} as const;
